import clsx from 'clsx';
import { useState } from 'react';
import { Icon } from '@/components/Icon';
import type { ResultRow, Results } from './types';

// Award colours for the usual names; any other award gets the neutral pill.
const AWARD_STYLES: Record<string, string> = {
  Алт: 'bg-yellow-200 text-yellow-900',
  Мөнгө: 'bg-gray-100 text-gray-700',
  Хүрэл: 'bg-orange-100 text-orange-800',
};

function AwardPill({ award }: { award: string }) {
  return (
    <span
      className={clsx(
        'inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold',
        AWARD_STYLES[award] ?? 'bg-green-100 text-green-700',
      )}
    >
      {award}
    </span>
  );
}

function Row({ row }: { row: ResultRow }) {
  const name = [row.last_name, row.first_name].filter(Boolean).join(' ');
  return (
    <li className="flex items-center gap-3 px-4 py-3 sm:gap-4 sm:px-6">
      <span
        className={clsx(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold',
          row.place <= 3 ? 'bg-green-600 text-white' : 'bg-green-50 text-green-800',
        )}
      >
        {row.place}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold text-green-900">{name}</span>
        {row.school_name && (
          <span className="block truncate text-sm text-green-800">{row.school_name}</span>
        )}
      </span>
      {row.award && <AwardPill award={row.award} />}
      <span className="w-14 flex-shrink-0 text-right text-lg font-bold tabular-nums text-green-900">
        {row.total_score}
      </span>
    </li>
  );
}

// Results (CONTEXT.md): each class year's top Participants by Total score,
// with their place and any award, one class year at a time.
export default function ResultsSection({ results }: { results: Results }) {
  const [classId, setClassId] = useState(results.class_years[0]?.id);
  const current = results.class_years.find((cy) => cy.id === classId) ?? results.class_years[0];
  if (!current) return null;

  return (
    <section className="mt-10" aria-labelledby="results-heading">
      <h2
        id="results-heading"
        className="flex items-center gap-2 text-2xl font-bold text-green-900"
      >
        <Icon icon="starFilled" className="h-6 w-6 text-yellow-500" />
        Үр дүн
      </h2>

      {results.class_years.length > 1 && (
        <div role="tablist" className="mt-4 flex gap-2 overflow-x-auto pb-1">
          {results.class_years.map((cy) => (
            <button
              key={cy.id}
              type="button"
              role="tab"
              aria-selected={cy.id === current.id}
              onClick={() => setClassId(cy.id)}
              className={clsx(
                'whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold duration-200 ease-in-out',
                cy.id === current.id
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-green-800 shadow hover:text-green-600',
              )}
            >
              {cy.name}
            </button>
          ))}
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-3xl bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-green-50 px-4 py-3 text-sm font-medium text-green-700 sm:px-6">
          <span>
            {current.name} · {current.results.length} оролцогч
          </span>
          <span>Нийт оноо ({current.max_score}-аас)</span>
        </div>
        <ol className="divide-y divide-green-50">
          {current.results.map((row, index) => (
            <Row key={`${row.first_name}-${index}`} row={row} />
          ))}
        </ol>
      </div>
    </section>
  );
}
